import React, { useContext, useState } from 'react';
import { graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import { getShopifyImage } from 'gatsby-source-shopify';
import styled, { css } from 'styled-components';
import {
  brandColours,
  fontSize,
  minBreakpointQuery,
  standardColours,
  standardTransition,
} from '../styles';
import { Button, Link } from './ui';
import { ProductPrice } from './Prices';
import { StoreContext } from '../context/StoreContext';
import tempImage from '../images/temp-image.png';

// moved up for hover
const StyledButton = styled(Button)`
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  background-color: ${brandColours.octonary};
  transform: translateY(100%);
  transition: ${standardTransition('background-color')},
    ${standardTransition('transform')};

  &:disabled {
    opacity: 0.6;
  }
`;

const StyledProductCard = styled.article``;

const StyledImageWrapper = styled.div`
  position: relative;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${standardColours.white};
  border: 1px solid ${standardColours.lightestGrey};
  border-radius: 15px;
  transform: translateZ(0);

  &:hover {
    ${StyledButton} {
      transform: translateY(0);
    }
  }
`;

const commonImageStyles = () => {
  return css`
    margin: 25px 10px;
    max-width: 215px;
    max-height: 215px;

    ${minBreakpointQuery.medium`
      margin: 30px 15px;
    `}

    ${minBreakpointQuery.large`
      max-width: 235px;
      max-height: 235px;
    `}

    ${minBreakpointQuery.xxlarge`
      max-width: 265px;
      max-height: 265px;
    `}
  `;
};

const StyledImage = styled(GatsbyImage)`
  ${commonImageStyles()};
`;

const StyledTempImage = styled.img`
  ${commonImageStyles()};
`;

const StyledTitle = styled.h3`
  margin-top: 14px;
  ${fontSize(15)};
  line-height: 1.2;

  ${minBreakpointQuery.large`
    margin-top: 16px;
    ${fontSize(16)};
  `}

  ${minBreakpointQuery.xxlarge`
    margin-top: 18px;
    ${fontSize(17)};
  `}
`;

const StyledProductReviews = styled.div`
  margin-top: 5px;
`;

const StyledPrice = styled(ProductPrice)`
  margin-top: 4px;
  ${fontSize(15)};

  ${minBreakpointQuery.large`
    ${fontSize(16)};
  `}
`;

const ProductCard = product => {
  const {
    shopifyId,
    image,
    title,
    priceRangeV2,
    hasOnlyDefaultVariant,
    variants,
    m2Pricing,
    productBreadcrumbs,
    ...props
  } = product;

  const imageData =
    image &&
    image.src &&
    getShopifyImage({
      image: { originalSrc: image.src },
      width: 265,
      height: 265,
    });

  const { addProductsToCheckout, toggleCartOpen } = useContext(StoreContext);

  const [addingToBasket, setAddingToBasket] = useState(false);

  const handleClick = async () => {
    const lineItems = [
      {
        variantId: variants[0].storefrontId,
        quantity: 1,
      },
    ];

    setAddingToBasket(true);

    await addProductsToCheckout(lineItems);

    setAddingToBasket(false);

    toggleCartOpen();
  };

  return (
    <StyledProductCard {...props}>
      <StyledImageWrapper>
        <Link to={product} state={{ productBreadcrumbs: productBreadcrumbs }}>
          {image && image.gatsbyImageData ? (
            <StyledImage
              image={image.gatsbyImageData}
              alt={image.altText || title}
            />
          ) : image && image.src ? (
            <StyledImage image={imageData} alt={image.altText || title} />
          ) : (
            <StyledTempImage src={tempImage} alt="Temp image" />
          )}
        </Link>
        <StyledButton
          {...(hasOnlyDefaultVariant
            ? {
                disabled: addingToBasket,
                onClick: () => handleClick(),
              }
            : { to: product })}
          state={
            !hasOnlyDefaultVariant && { productBreadcrumbs: productBreadcrumbs }
          }
        >
          {addingToBasket
            ? 'Adding...'
            : hasOnlyDefaultVariant
              ? 'Add to Basket'
              : 'View Product'}
        </StyledButton>
      </StyledImageWrapper>
      <StyledTitle>
        <Link to={product} state={{ productBreadcrumbs: productBreadcrumbs }}>
          {title}
        </Link>
      </StyledTitle>
      {shopifyId && (
        <StyledProductReviews
          className="klaviyo-star-rating-widget"
          data-id={shopifyId.replace('gid://shopify/Product/', '')}
          data-product-title={title}
        ></StyledProductReviews>
      )}
      <StyledPrice
        priceRange={priceRangeV2}
        variants={variants}
        m2Pricing={m2Pricing && m2Pricing.value}
      />
    </StyledProductCard>
  );
};

export default ProductCard;

export const ProductCardFragment = graphql`
  fragment ProductCardFragment on ShopifyProduct {
    shopifyId
    title
    image: featuredImage {
      gatsbyImageData(width: 265)
      altText
    }
    priceRangeV2 {
      minVariantPrice {
        amount
      }
      maxVariantPrice {
        amount
      }
    }
    hasOnlyDefaultVariant
    variants {
      storefrontId
      price
      compareAtPrice
    }
    m2Pricing: metafield(key: "m2_pricing", namespace: "custom") {
      value
    }
    ...LinkFragment
  }
`;
